import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ANKH_PATHS, ANKH_JEWEL } from "@/lib/ankh";

const [LOOP, ARMS, STEM, PETAL, SIDES] = ANKH_PATHS;
const EASE = [0.65, 0, 0.35, 1];
const trace = (delay, duration) => ({
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: { pathLength: { delay, duration, ease: EASE }, opacity: { delay, duration: 0.2 } },
});
const bloom = (delay, rotate = 0) => ({
  initial: { scale: 0, rotate, opacity: 0 },
  animate: { scale: 1, rotate: 0, opacity: 1 },
  transition: { delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  style: { transformBox: "fill-box", originX: "50%", originY: "100%" },
});
const [LEFT, RIGHT] = SIDES.split(" M").map((d, i) => (i ? `M${d}` : d));

const AnkhTrace = ({ still }) => {
  if (still) {
    return (
      <svg viewBox="-50 -50 100 100" className="w-28 h-28" aria-hidden="true">
        <g fill="none" stroke="#D9B47A" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
          {ANKH_PATHS.map((d) => <path key={d} d={d} />)}
        </g>
        <circle {...ANKH_JEWEL} fill="#C2414F" />
      </svg>
    );
  }
  return (
    <svg data-testid="loader-ankh" viewBox="-50 -50 100 100" className="w-28 h-28 overflow-visible" aria-hidden="true">
      <defs>
        <radialGradient id="lotus-glow">
          <stop offset="0" stopColor="#F4D9A4" stopOpacity="0.55" />
          <stop offset="1" stopColor="#D9B47A" stopOpacity="0" />
        </radialGradient>
      </defs>
      <motion.circle cx="0" cy="-26" r="16" fill="url(#lotus-glow)" initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: [0, 1, 0.6], scale: 1 }} transition={{ delay: 1.75, duration: 1.2 }} />
      <g fill="none" stroke="#D9B47A" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round">
        <motion.path d={LOOP} {...trace(0, 0.8)} />
        <motion.path d={ARMS} {...trace(0.6, 0.6)} />
        <motion.path d={STEM} {...trace(1.0, 0.6)} />
      </g>
      <motion.circle {...ANKH_JEWEL} fill="#C2414F" initial={{ scale: 0 }} animate={{ scale: [0, 1.5, 1] }} transition={{ delay: 1.5, duration: 0.5 }} style={{ transformBox: "fill-box", originX: "50%", originY: "50%" }} />
      <g fill="rgba(217,180,122,0.18)" stroke="#D9B47A" strokeWidth="1.6" strokeLinejoin="round">
        <motion.path d={LEFT} {...bloom(1.95, 35)} />
        <motion.path d={RIGHT} {...bloom(1.95, -35)} />
        <motion.path d={PETAL} {...bloom(1.7)} />
      </g>
    </svg>
  );
};

export default function Loader({ show = true }) {
  const still = useReducedMotion();
  const [open, setOpen] = useState(show);
  useEffect(() => {
    const t = setTimeout(() => setOpen(false), still ? 1200 : 3100);
    return () => clearTimeout(t);
  }, [still]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="intro-loader"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#0A0706]"
          exit={{ clipPath: "inset(0 0 100% 0)" }}
          initial={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
        >
          <AnkhTrace still={still} />
          <motion.p
            initial={{ opacity: 0, letterSpacing: "0.1em" }}
            animate={{ opacity: 1, letterSpacing: "0.5em" }}
            transition={{ delay: still ? 0 : 1.2, duration: 1.4 }}
            className="font-tt-mono text-xs uppercase text-[#D9B47A] mt-8"
          >
            Enter slowly
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
